<template>
	<div class="ir-achievement">
		<div class="quarter-tabs">
			<div
				class="quarter-tab"
				:class="{ 'quarter-tab-active': item.active, 'quarter-tab-disabled': item.disabled }"
				v-for="item in showQuarter"
				:key="item.key"
				@click="changeQuarter(item)"
			>
				{{ item.label }}
			</div>
		</div>
		<div class="year-tabs">
			<div
				class="prev-btn"
				:class="{ 'disabled-btn': yearList.length == 0 || activeYear == 0 }"
				@click="handlePrev()"
			>
				<i class="iconfont iconjiantouyou"></i>
			</div>
			<p class="curr-year">{{ currYear }}{{ $t('enterprise.year') }}</p>
			<div
				class="next-btn"
				:class="{ 'disabled-btn': yearList.length == 0 || activeYear == yearList.length - 1 }"
				@click="handleNext()"
			>
				<i class="iconfont iconjiantouyou"></i>
			</div>
		</div>
		<div class="main">
			<div class="title">
				<p class="enterprise-name">
					{{ getIrName }}<span class="enterprise-code">（{{ getIrBaseInfo.code }}）</span>
				</p>
				<p class="achievement-date">{{ currQuarter.announcementTitle }}</p>
				<img class="achievement-bg" src="@/assets/images/enterprise/ir-achievement.png" alt="" />
			</div>

			<!-- 业绩路演 -->
			<RoadshowSection :quarterInfo="currQuarter" />
			<!-- 一图读懂 -->
			<ImagePreviewSection :quarterInfo="currQuarter" title="业绩材料" />
			<!-- 业绩材料 -->
			<MaterialSection :quarterInfo="currQuarter" title="定期报告" />
			<!-- 总结展望 -->
			<SummarySection :quarterInfo="currQuarter" />
		</div>

		<m-visible-tips />
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { useStore, useRoute, useRouter } from '@/utils/vue-api';
import RoadshowSection from '@rebuild/materials/shared/quarterly_performance/roadshow-section.vue';
import ImagePreviewSection from '@rebuild/materials/shared/quarterly_performance/image-preview-section.vue';
import MaterialSection from '@rebuild/materials/shared/quarterly_performance/material-section.vue';
import SummarySection from '@rebuild/materials/shared/quarterly_performance/summary-section.vue';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
// 获取组件实例
const { proxy } = getCurrentInstance();
const store = useStore();
const route = useRoute();
const router = useRouter();

// 响应式数据
const activeQuarter = ref(0);
const activeYear = ref(new Date().getFullYear());
const storeId = route.query.storeId;
const performance = ref({});

// 计算属性
const getIrBaseInfo = computed(() => store.getters.getIrBaseInfo);
const getIrName = computed(() => store.getters.getIrName);

const yearList = computed(() => {
	return Object.keys(performance.value).sort((a, b) => a - b);
});

const currYear = computed(() => {
	return yearList.value[activeYear.value];
});

const quarterList = computed(() => {
	return (performance.value[currYear.value] || []).map(ele => ele.quarter);
});

const showQuarter = computed(() => {
	return [
		{
			label: proxy.$t('enterprise.results.annual'),
			key: 4
		},
		{
			label: proxy.$t('enterprise.results.third-quarter'),
			key: 3
		},
		{
			label: proxy.$t('enterprise.results.interim'),
			key: 2
		},
		{
			label: proxy.$t('enterprise.results.first-quarter'),
			key: 1
		}
	]
		.map(ele => {
			let item = (performance.value[currYear.value] || []).find(item => item.quarter == ele.key);
			ele.active = activeQuarter.value == ele.key;
			ele.disabled = !quarterList.value.includes(ele.key);
			return {
				...item,
				...ele
			};
		})
		.reverse();
});

const currQuarter = computed(() => {
	return showQuarter.value.find(ele => ele.active) || {};
});

// 监听器
watch(quarterList, val => {
	if (!val.includes(activeQuarter.value)) {
		activeQuarter.value = val[0] || 0;
	}
});

watch(yearList, val => {
	activeYear.value = val.length - 1;
});

// 方法
const getPerformanceTitle = prop => {
	if (prop === 't1') {
		return proxy.$t('enterprise.page-version');
	}
	if (prop === 't2') {
		return proxy.$t('enterprise.earnings-library');
	}
};

const { quarterly_performance } = useModuleData();
const refresh = async () => {
	await quarterly_performance.refresh();
	performance.value = quarterly_performance.data?.data;
};

const changeQuarter = item => {
	if (item.active || item.disabled) {
		return;
	}
	activeQuarter.value = item.key;
};

const handleNext = () => {
	if (activeYear.value == yearList.value.length - 1) {
		return;
	}
	activeYear.value++;
};

const handlePrev = () => {
	if (activeYear.value == 0) {
		return;
	}
	activeYear.value--;
};

const gotoFinancialData = () => {
	router.push({
		name: 'enterprise_ir_finance_data',
		query: {
			storeId: storeId
		}
	});
};

// 生命周期
onMounted(() => {
	refresh();
});
</script>

<style lang="less" scoped>
@import '@/style/mixin/mixins';
@import '@/style/mixin/border-radius';
@import '@/style/mixin/text-overflow';
.ir-achievement {
	width: 100%;
	background: linear-gradient(198deg, #c7d5f9 12.25%, #f6f7f8 56.2%);
	overflow: hidden;

	.quarter-tabs {
		margin: 0.12rem auto 0;
		width: 3.02rem;
		height: 0.32rem;
		padding: 0 0.08rem;
		border-radius: 0.2rem;
		background: #fff;

		display: flex;
		align-items: center;
		justify-content: space-between;

		.quarter-tab {
			width: 0.64rem;
			height: 0.24rem;

			color: #333;
			font-family: 'PingFang SC';
			font-size: 0.12rem;
			font-style: normal;
			font-weight: 400;
			line-height: 0.24rem;
			text-align: center;

			&-active {
				border-radius: 0.4rem;
				background: var(--ir-theme-color);
				color: #fff;
			}
			&-disabled {
				color: #c5c5c5;
			}
		}
		.quarter-tab + .quarter-tab {
			position: relative;
			margin-left: 0.1rem;

			&::before {
				content: '';
				position: absolute;
				top: 50%;
				left: -0.04rem;
				transform: translate(-100%, -50%);
				width: 0.02rem;
				height: 0.02rem;
				border-radius: 50%;
				background: var(--ir-theme-color-opacity-4);
			}
		}
	}

	.year-tabs {
		margin-top: 0.2rem;
		display: flex;
		align-items: center;
		justify-content: center;

		.prev-btn,
		.next-btn {
			width: 0.2rem;
			height: 0.2rem;
			border-radius: 0.02rem;
			background: rgba(255, 255, 255, 0.4);

			display: flex;
			align-items: center;
			justify-content: center;

			&.disabled-btn {
				.iconfont {
					color: #c5c5c5;
				}
			}

			.iconfont {
				font-size: 0.1rem;
			}
		}
		.prev-btn {
			.iconjiantouyou {
				transform: rotate(180deg);
			}
		}
		.curr-year {
			margin: 0 0.48rem;
			color: #333;
			font-family: 'PingFang SC';
			font-size: 0.15rem;
			font-style: normal;
			font-weight: 500;
		}
	}

	.main {
		padding: 0 0.14rem;
		overflow: hidden;
	}

	.title {
		position: relative;
		margin-top: 0.36rem;
		padding-left: 0.12rem;
		padding-bottom: 0.2rem;

		color: #333;
		font-family: MiSans;
		font-size: 0.22rem;
		font-style: normal;
		font-weight: 600;
		line-height: 0.3rem;

		.enterprise-code {
			font-size: 0.14rem;
		}
		.achievement-date {
			margin-top: 0.08rem;
			padding-right: 1.28rem;
		}

		.achievement-bg {
			position: absolute;
			top: -0.34rem;
			right: 0;
			width: 1.28rem;
			height: 1.28rem;
		}
	}
}
</style>
